import { clipText } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTableColumn, VALTYPES } from '../../../../data/interfaces';
import { getColumnInfo } from '../../../../data/analyticsDataSlice';
import { getNumericalColorSet } from '../../helpers/colors';
import { getSelectionHighlightColor, getStandardDataColor } from '../../../themes/themes';
import { LEGEND_ITEM_TYPES, renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import { chartDims, convertColumnDataToDisplayString, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { sunBurstMaxLevelCount } from './sunBurst.Manifest';
import { getMultiLevelCatEnFilterStep } from '../../../../data/selection/selectionEnFilterMultiLevelCat';
import { currentTheme, themedLighen } from '../../../themes/themes';
import { createNumericalScaleStops } from '../../helpers/colorMappingInfo';

interface TpSegment {
  key: string;
  name: string;
  fullName: string;
  parentLevelKey: string | null;
  startAngle: number;
  relativeSize: number;
  relativeSizeSelected: number;
  itemCount: number;
  itemCountSelected: number;
  colorNumericalValue: number;
  angle: number;
  itemNumbers: number[];
}

interface TpLevel {
  parentLevel: TpLevel | null;
  index: number;
  colId: string;
  columnInfo: TpDataTableColumn;
  data: any[];
  segments: TpSegment[];
  segmentMap: { [id: string]: TpSegment };
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable = boundData.dataTable;
  const dataSummedAngle = boundData.bindings.angle?.data;
  const dataColor = boundData.bindings.color && boundData.bindings.color.data;
  const dataItemId = boundData.bindings.itemId.data;

  const hasNumericalColors =
    dataColor &&
    [VALTYPES.NUMBER, VALTYPES.INTEGER].includes(getColumnInfo(dataTable, boundData.bindings.color.columnId).valueType);
  let colorLabel = '';
  if (dataColor) colorLabel = boundData.bindings.color.columnInfo.name;

  const absentCoding = 'NA';
  const burstRadius = 0.3;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  let parentLevel = null;
  const levels: TpLevel[] = [];
  for (let levelIdx = 0; levelIdx < sunBurstMaxLevelCount; levelIdx++) {
    const levelid = `level_${levelIdx + 1}`;
    if (!boundData.bindings[levelid] || !boundData.bindings[levelid].data) break;
    const level: TpLevel = {
      parentLevel,
      index: levelIdx,
      colId: boundData.bindings[levelid].columnId,
      data: boundData.bindings[levelid].data,
      columnInfo: boundData.bindings[levelid].columnInfo,
      segments: [],
      segmentMap: {},
    };
    levels.push(level);
    parentLevel = level;
  }
  const levelCount = levels.length;

  const filterInfo = getMultiLevelCatEnFilterStep(
    boundData.canonicalTypeInfo.selectionFilter,
    boundData.dataTable.id,
    levels.map((level) => level.colId),
  );

  // attribute all data items to all level segments
  for (let itemIdx = 0; itemIdx < dataItemId.length; itemIdx++) {
    let parentLevelKey = null;
    let key = '';
    let fullName = '';
    for (const level of levels) {
      const name = convertColumnDataToDisplayString(level.columnInfo, level.data[itemIdx]);
      if (key != '') key += '|';
      key += name;
      if (fullName != '') fullName = name + ', ' + fullName;
      else fullName = name;
      if (!level.segmentMap[key]) {
        const newSegment: TpSegment = {
          key,
          name,
          fullName,
          parentLevelKey,
          relativeSize: 0,
          relativeSizeSelected: 0,
          startAngle: 0,
          angle: 0,
          itemCount: 0,
          itemCountSelected: 0,
          colorNumericalValue: 0,
          itemNumbers: [],
        };
        level.segmentMap[key] = newSegment;
        level.segments.push(newSegment);
      }
      const segment = level.segmentMap[key];
      segment.itemNumbers.push(itemIdx);
      if (dataSummedAngle) segment.relativeSize += dataSummedAngle[itemIdx];
      else segment.relativeSize += 1;
      segment.itemCount += 1;
      if (isSelectedItem(selectedItemsMap, dataItemId[itemIdx])) {
        if (dataSummedAngle) segment.relativeSizeSelected += dataSummedAngle[itemIdx];
        else segment.relativeSizeSelected += 1;
        segment.itemCountSelected += 1;
      }
      if (hasNumericalColors) segment.colorNumericalValue += dataColor[itemIdx];
      parentLevelKey = key;
    }
  }

  //normalise the relative size of segments per level
  for (const level of levels) {
    let totalSize = 0;
    for (const segment of level.segments) totalSize += segment.relativeSize;
    for (const segment of level.segments) {
      segment.relativeSize /= totalSize;
      segment.relativeSizeSelected /= totalSize;
    }
    if (hasNumericalColors) {
      for (const segment of level.segments) segment.colorNumericalValue /= segment.itemCount;
    }
  }

  let minColorValue = 1e99;
  let maxColorValue = -1e99;
  if (hasNumericalColors) {
    for (const level of levels)
      for (const segment of level.segments) {
        if (segment.colorNumericalValue < minColorValue) minColorValue = segment.colorNumericalValue;
        if (segment.colorNumericalValue > maxColorValue) maxColorValue = segment.colorNumericalValue;
      }
  }
  if (maxColorValue <= minColorValue) {
    maxColorValue = minColorValue + 0.5;
    minColorValue -= 0.5;
  }

  //herarchical sorting & spreading of segments

  function spreadSegments(levelIdx: number, parentSegmentKey: string | null, angleStart: number, angleSpread: number) {
    const segments: TpSegment[] = [];
    const level = levels[levelIdx];
    for (const segment of level.segments)
      if (!parentSegmentKey || segment.parentLevelKey == parentSegmentKey) segments.push(segment);
    segments.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    //segments.sort((a, b) => b.relativeSize - a.relativeSize);
    const emptyFrac = 0; //0.15 * (0.75 ** levelIdx);
    let currentAngle = angleStart;
    let segmentIdx = 0;
    const totalSize = segments.reduce((acc, val) => acc + val.relativeSize, 0);
    for (const segment of segments) {
      segment.startAngle = currentAngle;
      const frc = (segment.relativeSize / totalSize) * (1 - (emptyFrac * (segments.length - 1)) / segments.length);
      segment.angle = frc * angleSpread;

      if (levelIdx < levelCount - 1) spreadSegments(levelIdx + 1, segment.key, currentAngle, segment.angle);
      currentAngle += (frc + emptyFrac / segments.length) * angleSpread;
      segmentIdx++;
    }
  }

  spreadSegments(0, null, 0, 2 * Math.PI);

  let colors;
  let colorValueStrings: any = null;
  let colorValueStringsUnsel: any = null;
  if (hasNumericalColors) {
    colors = getNumericalColorSet(config.colorSchemeNumerical).colors;
    colorValueStrings = colors.map((color) => color.toCanvasString());
    colorValueStringsUnsel = colors.map((color) => themedLighen(color.desaturate(0.5), 0.5).toCanvasString());
  }

  function renderSunBurst(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, mapsize, pixelConv, logicalRangeX, logicalRangeY } = renderContext;
    const ctx = renderContext.canvasContext;

    const logicalRange = 1; //Math.min(logicalRangeX.rmax - logicalRangeX.rmin, logicalRangeY.rmax - logicalRangeY.rmin);

    ctx.lineWidth = renderContext.pixelRatio * 0.5;

    const standardDataColorString = getStandardDataColor().opacity(1).toCanvasString();
    const standardDataColorUnselString = getStandardDataColor().lighten(0.5).toCanvasString();

    for (const level of levels) {
      if (level.index > -1) {
        const rd1 = ((level.index + 0.5) / (levelCount + 0.5)) * burstRadius;
        const rd2 = ((level.index + 1.5) / (levelCount + 0.5)) * burstRadius;
        const rd1Sel = ((level.index + 0.5 + 0.2) / (levelCount + 0.5)) * burstRadius;
        const rd2Sel = ((level.index + 1.5 - 0.2) / (levelCount + 0.5)) * burstRadius;
        ctx.fillStyle = standardDataColorString;
        const rd1Render = mapx(rd1) - mapx(0) + mapsize(1);
        const rd2Render = mapx(rd2) - mapx(0);
        const rd1RenderSel = mapx(rd1Sel) - mapx(0) + mapsize(1);
        const rd2RenderSel = mapx(rd2Sel) - mapx(0);
        const angWhiteSpace = mapsize(1) / rd2Render;
        for (const segment of level.segments) {
          const isFullySelected = segment.itemCountSelected == segment.itemCount;
          let isExtensionOfAbset = false; // if true, it means that this segment belongs to an "absent" segment on a higher level already, and hence should not be drawn
          if (segment.name == absentCoding) {
            if (level.parentLevel) {
              if (level.parentLevel.segmentMap[segment.parentLevelKey!].name == absentCoding) isExtensionOfAbset = true;
            }
          }
          if (!isExtensionOfAbset) {
            let colorIdx = null;
            if (hasNumericalColors) {
              const colorFrac = (segment.colorNumericalValue - minColorValue!) / (maxColorValue! - minColorValue!);
              colorIdx = Math.round(
                Math.max(0, Math.min(colorValueStrings!.length - 1, colorFrac * colorValueStrings!.length)),
              );
              ctx.fillStyle = isFullySelected ? colorValueStrings![colorIdx] : colorValueStringsUnsel![colorIdx];
            } else {
              ctx.fillStyle = isFullySelected ? standardDataColorString : standardDataColorUnselString;
            }
            const ang1 = segment.startAngle - Math.PI / 2;
            let ang2 = segment.startAngle + segment.angle - Math.PI / 2;
            if (level.segments.length > 1) ang2 = Math.max(ang1 + 1e-9, ang2 - angWhiteSpace);
            ctx.beginPath();
            ctx.arc(mapx(0.5), mapy(0.5), rd1Render, ang1, ang2, false);
            ctx.arc(mapx(0.5), mapy(0.5), rd2Render, ang2, ang1, true);
            ctx.closePath();
            ctx.fill();
            if (!isFullySelected) {
              const selFrac = segment.relativeSizeSelected / segment.relativeSize;
              const ang1Sel = (ang1 + ang2) / 2 - (ang2 - ang1) * (selFrac / 2);
              const ang2Sel = (ang1 + ang2) / 2 + (ang2 - ang1) * (selFrac / 2);
              if (hasNumericalColors) ctx.fillStyle = colorValueStrings![colorIdx!];
              else ctx.fillStyle = standardDataColorString;
              ctx.beginPath();
              ctx.arc(mapx(0.5), mapy(0.5), rd1RenderSel, ang1Sel, ang2Sel, false);
              ctx.arc(mapx(0.5), mapy(0.5), rd2RenderSel, ang2Sel, ang1Sel, true);
              ctx.closePath();
              ctx.fill();
            }

            if (filterInfo && filterInfo.selectedElements.map((el) => el.key).includes(segment.key)) {
              ctx.strokeStyle = getSelectionHighlightColor().toCanvasString();
              ctx.lineWidth = pixelConv(2);
              ctx.beginPath();
              ctx.arc(mapx(0.5), mapy(0.5), rd1Render, ang1, ang2, false);
              ctx.arc(mapx(0.5), mapy(0.5), rd2Render, ang2, ang1, true);
              ctx.closePath();
              ctx.stroke();
            }
          }
        }
      }
    }

    // Draw labels outermost level
    const magSize = mapx(1) - mapx(0);
    const minAngle = ((1000 / 360) * 2 * Math.PI) / magSize;
    const labels: any[] = [];
    const lastLevel = levels[levelCount - 1];
    for (const segment of lastLevel.segments) {
      const radius = ((lastLevel.index + 1.5) / (levelCount + 0.5)) * burstRadius;
      if (segment.angle > minAngle) {
        labels.push({
          xOrig: 0.5 + radius * Math.sin(segment.startAngle + segment.angle / 2),
          yOrig: 0.5 + radius * Math.cos(segment.startAngle + segment.angle / 2),
          angle: segment.startAngle + segment.angle / 2,
          label: segment.fullName,
        });
      }
    }
    labels.sort((a, b) => b.angle - a.angle);
    const q1Labels: any[] = [];
    const q2Labels: any[] = [];
    const q3Labels: any[] = [];
    const q4Labels: any[] = [];
    for (const label of labels) {
      if (label.angle < Math.PI / 2) q1Labels.push(label);
      else {
        if (label.angle < Math.PI) q2Labels.unshift(label);
        else {
          if (label.angle < (3 / 2) * Math.PI) q3Labels.push(label);
          else q4Labels.unshift(label);
        }
      }
    }

    const fontSize = 7;
    const separation = 2.3 * fontSize;
    ctx.strokeStyle = currentTheme.colors.text.toCanvasString();
    ctx.fillStyle = currentTheme.colors.text.toCanvasString();
    const offset = mapsize(10);
    renderContext.setFont(fontSize);
    const xMin = renderContext.mapx(renderContext.logicalRangeX.rmin) + mapsize(4);
    const xMax = renderContext.mapx(renderContext.logicalRangeX.rmax) - mapsize(4);
    let lastY = 1e99;
    for (const labelItem of q1Labels) {
      ctx.beginPath();
      ctx.moveTo(mapx(labelItem.xOrig), mapy(labelItem.yOrig));
      const xl = mapx(labelItem.xOrig) + offset * Math.sin(labelItem.angle);
      let yl = mapy(labelItem.yOrig) - offset * Math.cos(labelItem.angle);
      if (yl >= lastY) yl = lastY;
      ctx.lineTo(xl, yl);
      ctx.stroke();
      const maxWidth = xMax - xl;
      const labelClipped = clipText(ctx, labelItem.label, maxWidth, fontSize);
      ctx.fillText(labelClipped, xl + mapsize(2), yl - mapsize(fontSize - 2));
      lastY = yl - separation;
    }
    lastY = -1e99;
    for (const labelItem of q2Labels) {
      ctx.beginPath();
      ctx.moveTo(mapx(labelItem.xOrig), mapy(labelItem.yOrig));
      const xl = mapx(labelItem.xOrig) + offset * Math.sin(labelItem.angle);
      let yl = mapy(labelItem.yOrig) - offset * Math.cos(labelItem.angle);
      if (yl <= lastY) yl = lastY;
      ctx.lineTo(xl, yl);
      ctx.stroke();
      const maxWidth = xMax - xl;
      const labelClipped = clipText(ctx, labelItem.label, maxWidth, fontSize);
      ctx.fillText(labelClipped, xl + mapsize(2), yl - mapsize(fontSize - 2));
      lastY = yl + separation;
    }

    ctx.textAlign = 'right';
    lastY = 1e99;
    for (const labelItem of q4Labels) {
      ctx.beginPath();
      ctx.moveTo(mapx(labelItem.xOrig), mapy(labelItem.yOrig));
      const xl = mapx(labelItem.xOrig) + offset * Math.sin(labelItem.angle);
      let yl = mapy(labelItem.yOrig) - offset * Math.cos(labelItem.angle);
      if (yl >= lastY) yl = lastY;
      ctx.lineTo(xl, yl);
      ctx.stroke();
      const maxWidth = xl - xMin;
      const labelClipped = clipText(ctx, labelItem.label, maxWidth, fontSize);
      ctx.fillText(labelClipped, xl - mapsize(2), yl - mapsize(fontSize - 2));
      lastY = yl - separation;
    }
    lastY = -1e99;
    for (const labelItem of q3Labels) {
      ctx.beginPath();
      ctx.moveTo(mapx(labelItem.xOrig), mapy(labelItem.yOrig));
      const xl = mapx(labelItem.xOrig) + offset * Math.sin(labelItem.angle);
      let yl = mapy(labelItem.yOrig) - offset * Math.cos(labelItem.angle);
      if (yl <= lastY) yl = lastY;
      ctx.lineTo(xl, yl);
      ctx.stroke();
      const maxWidth = xl - xMin;
      const labelClipped = clipText(ctx, labelItem.label, maxWidth, fontSize);
      ctx.fillText(labelClipped, xl - mapsize(2), yl - mapsize(fontSize - 2));
      lastY = yl + separation;
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (hasNumericalColors)
      legendItems.push({
        type: LEGEND_ITEM_TYPES.NumericalColors,
        aggregationMethod: 'average',
        colorColumnInfo: getColumnInfo(dataTable, boundData.bindings.color.columnId),
        colorValueStrings: colorValueStrings,
        minValColor: minColorValue,
        maxValColor: maxColorValue,
        gammaFactor: 1,
        numericalLegendItems: createNumericalScaleStops(minColorValue, maxColorValue),
      });
    renderLegendItems(renderContext, legendItems);
  }

  const hasLegend = hasNumericalColors;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(0, 1);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + legendBottomSize),
  );

  const areaSunBurst: TpChartAreaDefinition = {
    id: 'sunBurst',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    forceSquareApectRatio: true,
    render: renderSunBurst,
  };

  let title = generateChartTitle(
    dataContext,
    dataBinding,
    config,
    levels.map((level) => level.columnInfo.name).join(', '),
  );

  if (boundData.bindings.angle) title += ' / ' + boundData.bindings.angle.columnInfo.name;

  const chartAreas: TpChartAreaDefinition[] = [
    areaSunBurst,
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },
  ];

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    dataTable,
    areaSunBurst,
    dataItemId,
    levels,
    burstRadius,
    hasNumericalColors,
    colorLabel,
    title,
  };
}
